import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { inputCls, money, fmtMoney } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["Sponsorship", "Merch", "Events", "Content"];

const FIELDS = [
  { key: "label", label: "Description *", required: true, placeholder: "e.g. Apex Golf — July sponsorship" },
  { key: "category", label: "Category", type: "select", options: CATEGORIES },
  { key: "amount", label: "Amount ($)", type: "number", placeholder: "0.00" },
  { key: "date", label: "Date", type: "date" },
  { key: "status", label: "Status", type: "select", options: ["Received", "Outstanding"] },
  { key: "source", label: "From / Source", placeholder: "Who paid (or will pay)" },
  { key: "notes", label: "Notes", type: "textarea" },
];

const monthName = (key) => {
  const [y, m] = key.split("-");
  return new Date(Number(y), Number(m) - 1).toLocaleString("en-US", { month: "short", year: "numeric" });
};

const StatCard = ({ label, value, testid, valueCls }) => (
  <div className="border border-white/10 bg-ink-2 p-5" data-testid={testid}>
    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted">{label}</p>
    <p className={`mt-2 font-display text-3xl font-extrabold tracking-tight ${valueCls || "text-white"}`}>{value}</p>
  </div>
);

export default function RevenueTab({ token, onAuthFail }) {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const loadItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/board/revenue`, { headers });
      setItems(res.data.items);
    } catch (e) {
      if (e?.response?.status === 401) onAuthFail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const summary = useMemo(() => {
    const now = new Date();
    const mKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const yKey = String(now.getFullYear());
    let month = 0;
    let year = 0;
    let outstanding = 0;
    const byCat = Object.fromEntries(CATEGORIES.map((c) => [c, 0]));
    const byMonth = {};
    items.forEach((it) => {
      const amt = money(it.amount);
      const mk = (it.date || "").slice(0, 7);
      if (it.status === "Outstanding") {
        outstanding += amt;
        if (mk) (byMonth[mk] ||= { received: 0, outstanding: 0 }).outstanding += amt;
        return;
      }
      if (mk === mKey) month += amt;
      if ((it.date || "").startsWith(yKey)) year += amt;
      if (it.category in byCat) byCat[it.category] += amt;
      if (mk) (byMonth[mk] ||= { received: 0, outstanding: 0 }).received += amt;
    });
    const months = Object.entries(byMonth).sort((a, b) => b[0].localeCompare(a[0])).slice(0, 12);
    return { month, year, outstanding, byCat, months };
  }, [items]);

  const blankForm = () => {
    const f = {};
    FIELDS.forEach((fl) => {
      f[fl.key] = fl.type === "select" ? fl.options[0] : "";
    });
    return f;
  };

  const openNew = () => {
    setForm(blankForm());
    setModal({ mode: "new" });
  };

  const openEdit = (item) => {
    const f = blankForm();
    FIELDS.forEach((fl) => {
      if (item[fl.key] != null) f[fl.key] = item[fl.key];
    });
    setForm(f);
    setModal({ mode: "edit", item });
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (modal.mode === "new") {
        await axios.post(`${API}/admin/board/revenue`, { data: form }, { headers });
        toast.success("Entry added.");
      } else {
        await axios.patch(`${API}/admin/board/revenue/${modal.item.id}`, { data: form }, { headers });
        toast.success("Entry updated.");
      }
      setModal(null);
      await loadItems();
    } catch (e2) {
      if (e2?.response?.status === 401) onAuthFail();
      else toast.error("Could not save.");
    } finally {
      setBusy(false);
    }
  };

  const removeItem = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/board/revenue/${modal.item.id}`, { headers });
      toast.success("Deleted.");
      setModal(null);
      await loadItems();
    } catch (e2) {
      if (e2?.response?.status === 401) onAuthFail();
      else toast.error("Could not delete.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-8" data-testid="revenue-tab">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted" data-testid="revenue-count">
          Revenue · {items.length} entr{items.length === 1 ? "y" : "ies"}
        </p>
        <button onClick={openNew} data-testid="revenue-new" className="flex items-center gap-2 bg-white px-5 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-brand-accent">
          <Plus className="h-3.5 w-3.5" />
          New Entry
        </button>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-3" data-testid="revenue-summary">
        <StatCard label="This Month (Received)" value={fmtMoney(summary.month)} testid="revenue-summary-month" />
        <StatCard label="This Year (Received)" value={fmtMoney(summary.year)} testid="revenue-summary-year" />
        <StatCard label="Outstanding Payments" value={fmtMoney(summary.outstanding)} testid="revenue-summary-outstanding" valueCls={summary.outstanding > 0 ? "text-red-400" : "text-white"} />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="revenue-categories">
        {CATEGORIES.map((c) => (
          <StatCard key={c} label={`${c} (Received)`} value={fmtMoney(summary.byCat[c])} testid={`revenue-cat-${c.toLowerCase()}`} />
        ))}
      </div>

      {summary.months.length > 0 && (
        <div className="mt-8 border border-white/10 bg-ink-2" data-testid="revenue-monthly">
          <p className="border-b border-white/10 px-5 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-muted">Monthly Breakdown</p>
          <div className="divide-y divide-white/5">
            <div className="grid grid-cols-3 gap-4 px-5 py-3 font-mono text-[9px] uppercase tracking-[0.2em] text-white/30">
              <span>Month</span>
              <span className="text-right">Received</span>
              <span className="text-right">Outstanding</span>
            </div>
            {summary.months.map(([mk, v]) => (
              <div key={mk} className="grid grid-cols-3 gap-4 px-5 py-3 font-mono text-[11px]" data-testid={`revenue-month-${mk}`}>
                <span className="text-white">{monthName(mk)}</span>
                <span className="text-right text-white">{fmtMoney(v.received)}</span>
                <span className={`text-right ${v.outstanding > 0 ? "text-red-400" : "text-white/30"}`}>{fmtMoney(v.outstanding)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="mt-10 font-mono text-xs uppercase tracking-[0.25em] text-muted">All Entries</p>
      {items.length === 0 ? (
        <div className="mt-6 grid place-items-center gap-4 border border-dashed border-white/15 py-20 text-center">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-white/40">No revenue logged yet — add your first entry</p>
        </div>
      ) : (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="revenue-grid">
          {items.map((it) => (
            <button
              key={it.id}
              onClick={() => openEdit(it)}
              data-testid={`revenue-item-${it.id}`}
              className="border border-white/10 bg-ink-2 p-5 text-left transition-colors duration-300 hover:border-brand"
            >
              <div className="flex items-start justify-between gap-3">
                <h3 className="min-w-0 font-display text-lg font-extrabold uppercase leading-tight tracking-tight text-white">{it.label}</h3>
                <span className={`shrink-0 px-2.5 py-1 font-mono text-[9px] font-medium uppercase tracking-[0.15em] ${it.status === "Outstanding" ? "bg-red-500/20 text-red-400" : "bg-brand-accent text-ink"}`}>
                  {it.status}
                </span>
              </div>
              <div className="mt-3 flex flex-col gap-1.5">
                <p className="font-mono text-[11px] text-muted"><span className="text-white/35">Amount:</span> {fmtMoney(money(it.amount))}</p>
                {it.category && <p className="font-mono text-[11px] text-muted"><span className="text-white/35">Category:</span> {it.category}</p>}
                {it.source && <p className="font-mono text-[11px] text-muted"><span className="text-white/35">From:</span> {it.source}</p>}
                {it.date && <p className="font-mono text-[11px] text-muted"><span className="text-white/35">Date:</span> {it.date}</p>}
              </div>
            </button>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-[95] grid place-items-center bg-black/70 p-5 backdrop-blur-sm" data-testid="revenue-modal" onClick={() => setModal(null)}>
          <form data-lenis-prevent onSubmit={save} onClick={(e) => e.stopPropagation()} className="max-h-[85vh] w-full max-w-lg overflow-y-auto overscroll-contain border border-white/15 bg-ink-2 p-7 md:p-9" data-testid="revenue-form">
            <div className="flex items-center justify-between">
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand-accent">
                {modal.mode === "new" ? "New Revenue Entry" : "Edit Entry"}
              </p>
              <button type="button" onClick={() => setModal(null)} className="text-white/50 hover:text-white" aria-label="Close" data-testid="revenue-modal-close">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-6 flex flex-col gap-4">
              {FIELDS.map((field) => (
                <div key={field.key}>
                  <label htmlFor={`revenue-${field.key}`} className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-muted">{field.label}</label>
                  {field.type === "textarea" ? (
                    <textarea id={`revenue-${field.key}`} rows={3} value={form[field.key] || ""} onChange={(e) => setForm((f) => ({ ...f, [field.key]: e.target.value }))} placeholder={field.placeholder} data-testid={`revenue-field-${field.key}`} className={`${inputCls} w-full resize-none`} />
                  ) : field.type === "select" ? (
                    <select id={`revenue-${field.key}`} value={form[field.key] || field.options[0]} onChange={(e) => setForm((f) => ({ ...f, [field.key]: e.target.value }))} data-testid={`revenue-field-${field.key}`} className={`${inputCls} w-full appearance-none`}>
                      {field.options.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  ) : (
                    <input id={`revenue-${field.key}`} type={field.type || "text"} required={field.required} step={field.type === "number" ? "0.01" : undefined} min={field.type === "number" ? "0" : undefined} value={form[field.key] || ""} onChange={(e) => setForm((f) => ({ ...f, [field.key]: e.target.value }))} placeholder={field.placeholder} data-testid={`revenue-field-${field.key}`} className={`${inputCls} w-full ${field.type === "date" ? "[color-scheme:dark]" : ""}`} />
                  )}
                </div>
              ))}
            </div>

            <div className="mt-7 flex flex-wrap gap-3">
              <button type="submit" disabled={busy} data-testid="revenue-save" className="bg-brand-accent px-6 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-white disabled:opacity-60">
                {busy ? "Saving…" : modal.mode === "new" ? "Add" : "Save Changes"}
              </button>
              {modal.mode === "edit" && (
                <button type="button" onClick={removeItem} disabled={busy} data-testid="revenue-delete" className="border border-red-500/40 px-6 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-red-400 transition-colors duration-300 hover:bg-red-500/10 disabled:opacity-60">
                  Delete
                </button>
              )}
              <button type="button" onClick={() => setModal(null)} data-testid="revenue-cancel" className="border border-white/25 px-6 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-white/60">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
